import { Box, Button, CircularProgress, Divider, Toolbar, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavBar from './NavBar';
import { useAppSelector } from '../reduxhooks';
import { kodeUnikForOrder, normalizePaymentGroups, terbebasClient } from '../terbebasApi';

// Terbebas order detail / confirmation page (replaces the fareast PageOrder).
// Fetches the order from the storefront API by id.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  page: {
    width: '90%',
    maxWidth: '640px',
    margin: '30px auto 80px',
  },
  orderNumber: {
    fontSize: '26px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  statusChip: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.6px',
    padding: '4px 10px',
    borderRadius: '12px',
    backgroundColor: '#efefef',
    marginRight: '8px',
    marginBottom: '18px',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '10px 0px',
  },
  itemTitle: { fontSize: '14px', fontWeight: 500 },
  itemSub: { fontSize: '12px', fontWeight: 300, color: '#6a6a6a' },
  itemPrice: { fontSize: '14px', fontWeight: 400, whiteSpace: 'nowrap', marginLeft: '16px' },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '6px 0px',
  },
  addressBlock: {
    marginTop: '26px',
    fontSize: '13px',
    fontWeight: 300,
    lineHeight: 1.8,
  },
  trackingDot: {
    display: 'inline-block',
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    backgroundColor: '#343434',
    marginRight: '12px',
    flexShrink: 0,
  },
  trackingLine: {
    width: '1px',
    flexGrow: 1,
    minHeight: '18px',
    backgroundColor: '#c9c9c9',
    marginLeft: '4px',
  },
}));

// Same dedupe the fareast order page applied to KiriminAja histories.
function cleanUpHistories(histories: { status: string; created_at: string }[]) {
  const seen: { [key: string]: boolean } = {};
  return histories.filter((item) => {
    if (item.status === 'Pricing Updated' || seen[item.status]) return false;
    seen[item.status] = true;
    return true;
  });
}

const FINANCIAL_TEXT: { [key: string]: string } = {
  PENDING: 'MENUNGGU PEMBAYARAN',
  PAID: 'SUDAH DIBAYAR',
  PARTIALLY_REFUNDED: 'REFUND SEBAGIAN',
  REFUNDED: 'REFUND',
  VOIDED: 'DIBATALKAN',
};

const FULFILLMENT_TEXT: { [key: string]: string } = {
  UNFULFILLED: 'SEDANG DIPROSES',
  PARTIAL: 'SEBAGIAN DIKIRIM',
  FULFILLED: 'DIKEMAS',
  SHIPPED: 'DIKIRIM',
  DELIVERED: 'DITERIMA',
  RETURNED: 'DIKEMBALIKAN',
};

function formatRp(value: string | number) {
  return 'Rp ' + Math.round(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default function PageOrderCommerce() {
  const classes = useStyles();
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<any>(null);
  const [error, setError] = useState('');
  const [tracking, setTracking] = useState<any>(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState('');
  const paymentGroupsRaw = useAppSelector(state => state.storeProfile.profile?.config?.payments);
  // Manual-mode payment methods (account details) for the pending-order block.
  const manualMethods = normalizePaymentGroups(paymentGroupsRaw)
    .flatMap((g) => g.supportedMethods ?? [])
    .filter((m) => m.mode === 'manual' && m.method && m.accountNumber);
  // Static-QRIS methods only work once the store's QRIS image is uploaded.
  const qrisStaticImageUrl = useAppSelector(state =>
    ((state.storeProfile.profile?.config as any)?.qrisStaticImageUrl ?? '') as string);
  const qrisStaticMethods = qrisStaticImageUrl
    ? normalizePaymentGroups(paymentGroupsRaw)
      .flatMap((g) => g.supportedMethods ?? [])
      .filter((m) => m.mode === 'qris_static' && m.method)
    : [];
  const storeWhatsapp = useAppSelector(state =>
    ((state.storeProfile.profile?.config?.contact as any)?.whatsapp ?? '') as string);

  useEffect(() => {
    terbebasClient.get('/storefront/orders/' + orderId)
      .then((res) => setOrder(res.data))
      .catch(() => setError('Order tidak ditemukan.'));
  }, [orderId]);

  // ESPAY's backUrl lands buyers here possibly before the payment webhook
  // settles — refetch for a couple of minutes so the page flips to LUNAS
  // without a manual reload.
  useEffect(() => {
    if (!order || order.financialStatus !== 'PENDING' || order.paymentProvider !== 'espay') return;
    let ticks = 0;
    const interval = setInterval(() => {
      if (++ticks > 24) { clearInterval(interval); return; }
      terbebasClient.get('/storefront/orders/' + orderId)
        .then((res) => setOrder(res.data))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [order?.financialStatus, order?.paymentProvider, orderId]);

  // Live courier tracking once a resi/pickup exists.
  useEffect(() => {
    if (order && (order.trackingNumber || order.pickupRequestedAt)) {
      terbebasClient.get('/storefront/orders/' + orderId + '/tracking')
        .then((res) => setTracking(res.data))
        .catch(() => {});
    }
  }, [order, orderId]);

  // "Saya Sudah Bayar": records the claim (merchant reconciles the
  // kode-unik amount on their statement) and hands the buyer off to the
  // store's WhatsApp with the payment details.
  const submitClaim = async (provider: 'manual-transfer' | 'qris-static') => {
    if (claimBusy) return;
    setClaimBusy(true); setClaimError('');
    try {
      const res = await terbebasClient.post('/storefront/orders/' + orderId + '/payment-claim', { provider });
      // wa.me needs the international form; merchants often store 08xx.
      const waNumber = storeWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62');
      if (waNumber) {
        const waText = 'Permisi kak, saya baru bayar sebesar '
          + formatRp(Number(order.total) + kodeUnikForOrder(order.orderNumber))
          + (provider === 'qris-static' ? ' via QRIS' : ' via transfer manual')
          + ' untuk pembayaran order #' + order.orderNumber;
        window.open('https://wa.me/' + waNumber + '?text=' + encodeURIComponent(waText), '_blank');
      }
      setOrder({ ...order, ...res.data });
    } catch (e: any) {
      setClaimError(e?.response?.data?.error ?? 'Gagal menyimpan konfirmasi pembayaran');
    } finally { setClaimBusy(false); }
  };

  const showManualTransferBlock = order
    && order.financialStatus === 'PENDING'
    && (manualMethods.length > 0 || qrisStaticMethods.length > 0);
  const trackingHistories = Array.isArray(tracking?.histories)
    ? cleanUpHistories(tracking.histories) : [];

  return (
    <Box>
      <NavBar transparentAtTop={false} transparent={false} coloredAtTop={false} largerTitleAtTop={false} />
      <Toolbar />
      <Box className={classes.page}>
        {
          error ? <Typography>{error}</Typography> :
          !order ? <Box style={{textAlign: 'center', padding: '60px 0'}}><CircularProgress /></Box> :
          <>
            <Typography className={classes.orderNumber}>Order #{order.orderNumber}</Typography>
            <Typography className={classes.itemSub} style={{marginBottom: '14px'}}>
              {new Date(order.createdAt).toLocaleString('id-ID')}
            </Typography>
            <span className={classes.statusChip}>
              {FINANCIAL_TEXT[order.financialStatus] ?? order.financialStatus}
            </span>
            <span className={classes.statusChip}>
              {FULFILLMENT_TEXT[order.fulfillmentStatus] ?? order.fulfillmentStatus}
            </span>

            <Divider />
            {
              (order.items ?? []).map((item: any) => (
                <Box key={item.id} className={classes.itemRow}>
                  <Box>
                    <Typography className={classes.itemTitle}>{item.productTitle}</Typography>
                    <Typography className={classes.itemSub}>
                      {item.variantTitle !== 'Default Title' ? item.variantTitle + ' · ' : ''}x{item.quantity}
                    </Typography>
                  </Box>
                  <Typography className={classes.itemPrice}>
                    {formatRp(Number(item.price) * item.quantity)}
                  </Typography>
                </Box>
              ))
            }
            <Divider />

            <Box style={{marginTop: '16px'}}>
              <Box className={classes.totalRow}>
                <Typography className={classes.itemSub}>Subtotal</Typography>
                <Typography className={classes.itemSub}>{formatRp(order.subtotal)}</Typography>
              </Box>
              <Box className={classes.totalRow}>
                <Typography className={classes.itemSub}>Ongkos kirim</Typography>
                <Typography className={classes.itemSub}>{formatRp(order.shippingCost)}</Typography>
              </Box>
              <Box className={classes.totalRow}>
                <Typography className={classes.itemTitle}>Total</Typography>
                <Typography className={classes.itemTitle}>{formatRp(order.total)}</Typography>
              </Box>
            </Box>

            {
              order.shippingAddress &&
              <Box className={classes.addressBlock}>
                <b>Alamat pengiriman</b><br/>
                {order.shippingAddress.recipientName}<br/>
                {order.shippingAddress.addressDetail}<br/>
                {[order.shippingAddress.district, order.shippingAddress.city, order.shippingAddress.province].filter(Boolean).join(', ')} {order.shippingAddress.postalCode}<br/>
                {order.shippingAddress.phone}
              </Box>
            }
            {
              showManualTransferBlock &&
              <Box className={classes.addressBlock}>
                <b>{manualMethods.length > 0 ? 'Pembayaran via Transfer Bank' : 'Pembayaran via QRIS'}</b><br/>
                {
                  order.paymentProofUrl ?
                  <>
                    Bukti transfer sudah kami terima dan sedang diverifikasi penjual. 🙏<br/>
                    <a href={order.paymentProofUrl} target="_blank" rel="noreferrer">Lihat bukti transfer</a>
                  </> :
                  ['manual-transfer', 'qris-static'].includes(order.paymentProvider) ?
                  <>
                    Konfirmasi pembayaran kamu sudah kami terima dan sedang diverifikasi penjual. 🙏
                  </> :
                  <>
                    Bayar tepat sejumlah <b>{formatRp(Number(order.total) + kodeUnikForOrder(order.orderNumber))}</b>{' '}
                    (sudah termasuk kode unik {kodeUnikForOrder(order.orderNumber)} — sampai 3 digit
                    terakhir agar pembayaran cepat dikenali), lalu tekan "Saya Sudah Bayar".<br/>
                    {
                      manualMethods.map((account: any, index: number) => (
                        <span key={index}>
                          • {account.method} <b>{account.accountNumber}</b>
                          {account.accountName ? ' a.n. ' + account.accountName : ''}<br/>
                        </span>
                      ))
                    }
                    {
                      qrisStaticMethods.length > 0 &&
                      <>
                        {manualMethods.length > 0 && <span>Atau scan QRIS berikut:<br/></span>}
                        <img src={qrisStaticImageUrl} alt="QRIS"
                          style={{ maxWidth: '220px', width: '100%', display: 'block', margin: '8px 0px', border: '1px solid #e0e0e0', borderRadius: '6px' }} />
                        {
                          qrisStaticMethods[0].accountName &&
                          <span>Nama Penerima: <b>{qrisStaticMethods[0].accountName}</b><br/></span>
                        }
                      </>
                    }
                    <Button variant="outlined" size="small" disabled={claimBusy}
                      onClick={() => submitClaim(manualMethods.length > 0 ? 'manual-transfer' : 'qris-static')}
                      style={{ marginTop: '8px' }}>
                      {claimBusy ? 'MENGIRIM...' : 'SAYA SUDAH BAYAR'}
                    </Button>
                    {claimError && <span style={{ color: 'red', display: 'block' }}>{claimError}</span>}
                  </>
                }
              </Box>
            }
            {
              order.trackingNumber &&
              <Box className={classes.addressBlock}>
                <b>Resi</b>: {order.shippingCourier ? order.shippingCourier + ' — ' : ''}{order.trackingNumber}
              </Box>
            }
            {
              trackingHistories.length > 0 &&
              <Box className={classes.addressBlock}>
                <b>Lacak Pengiriman</b>
                <Box style={{ marginTop: '10px' }}>
                  {
                    trackingHistories.map((item: any, index: number) => (
                      <Box key={index} style={{ display: 'flex' }}>
                        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span className={classes.trackingDot}
                            style={index === 0 ? { backgroundColor: '#1a7f37' } : {}} />
                          {index !== trackingHistories.length - 1 && <span className={classes.trackingLine} />}
                        </Box>
                        <Box style={{ paddingBottom: '14px' }}>
                          <Typography style={{ fontSize: '13px', fontWeight: index === 0 ? 600 : 400 }}>
                            {item.status}
                          </Typography>
                          <Typography className={classes.itemSub}>
                            {item.created_at ? new Date(item.created_at.replace(' ', 'T')).toLocaleString('id-ID') : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  }
                </Box>
              </Box>
            }
          </>
        }
      </Box>
    </Box>
  );
}
